/**
 * The plan of a sheet's system-row swap (`Sheet.replaceSystemRows`): which
 * system rows to add, in what order and under which parent, given the
 * definitions `SystemRows.build` made and the surface anchors the sheet
 * already holds — a synthetic surface-container whose anchor (server-backed
 * or authored) exists is dropped and its children go under the anchor.
 *
 * Pure: it reads the definitions and the two anchor ids and refuses a
 * malformed set (a duplicate row, a child named twice or missing). The
 * sheet applies the plan (`addRow`, the tree reorder) and throws the cycle
 * the plan reports after applying it. It names no row type, so it sits
 * outside the rows' type knot.
 */

import type { Option } from "../core/option.ts";
import { Json } from "../core/json.ts";
import type { RowId } from "../ids/row-id.ts";

/** What the plan reads of a system row definition (`SystemRowDef`). */
export interface SystemRowPlanDef {
  readonly id: RowId;
  readonly type: string;
  readonly metadata: Option<Json>;
  readonly children?: readonly RowId[];
}

/** A system-row swap's plan (see the module note). */
export interface SystemRowsPlan<Def extends SystemRowPlanDef> {
  /** The rows to add, parent before child, each under its parent's raw id (`null`: a root). */
  readonly adds: readonly { readonly def: Def; readonly parentId: Option<string> }[];
  /** The raw ids of the rows reparented onto a surface anchor, to move to the anchor's front, in order. */
  readonly reparented: readonly string[];
  /** The first row the adds never reached (a cycle), `null` when every row is added. */
  readonly cycleAt: Option<string>;
}

/**
 * Plan the swap of `rows` under the existing surface anchors (`header`,
 * `footer`: an anchor row's raw id, `null` when the sheet has none). Throws
 * on a duplicate system row, a child that two rows name, or a child no row
 * defines.
 */
export function planSystemRows<Def extends SystemRowPlanDef>(
  rows: readonly Def[],
  anchors: { readonly header: Option<string>; readonly footer: Option<string> },
): SystemRowsPlan<Def> {
  const rowsById = new Map<string, Def>();
  const skipped = new Set<string>();
  const reparent = new Map<string, string>();

  for (const def of rows) {
    const id = def.id.toString();
    if (rowsById.has(id)) {
      throw new Error(
        `Sheet.replaceSystemRows: duplicate system row "${id}".`,
      );
    }
    // If this def is a synthetic surface-container AND an anchor
    // counterpart (server-backed or authored) exists at top
    // level, drop the synthetic and reparent its children onto
    // the anchor.
    if (def.type === "surface-container") {
      const surface = Json.string(Json.object(def.metadata ?? null), "surface");
      const anchorId = surface === "header"
        ? anchors.header
        : surface === "footer"
          ? anchors.footer
          : null;
      if (anchorId != null) {
        skipped.add(id);
        for (const childId of def.children ?? []) {
          reparent.set(childId.toString(), anchorId);
        }
        continue;
      }
    }
    rowsById.set(id, def);
  }

  const parentById = new Map<string, string>();
  for (const def of rowsById.values()) {
    const parentId = def.id.toString();
    for (const child of def.children ?? []) {
      const childId = child.toString();
      if (skipped.has(childId)) continue;
      if (!rowsById.has(childId)) {
        throw new Error(
          `Sheet.replaceSystemRows: row "${parentId}" references ` +
            `missing child "${childId}".`,
        );
      }
      const existingParent = parentById.get(childId);
      if (existingParent !== undefined) {
        throw new Error(
          `Sheet.replaceSystemRows: row "${childId}" is a child of ` +
            `both "${existingParent}" and "${parentId}".`,
        );
      }
      parentById.set(childId, parentId);
    }
  }

  const adds: { def: Def; parentId: Option<string> }[] = [];
  const added = new Set<string>();
  const addWithChildren = (def: Def, parentId: Option<string>) => {
    const id = def.id.toString();
    if (added.has(id)) return;
    adds.push({ def, parentId });
    added.add(id);
    for (const childId of def.children ?? []) {
      const childStr = childId.toString();
      if (skipped.has(childStr)) continue;
      const child = rowsById.get(childStr);
      if (child === undefined) {
        throw new Error(
          `Sheet.replaceSystemRows: missing child "${childStr}".`,
        );
      }
      addWithChildren(child, id);
    }
  };

  // Roots: rows that aren't a child of another included row.
  for (const def of rowsById.values()) {
    if (parentById.has(def.id.toString())) continue;
    // Reparented onto a surface anchor row?
    const anchorParent = reparent.get(def.id.toString());
    addWithChildren(def, anchorParent ?? null);
  }

  let cycleAt: Option<string> = null;
  for (const def of rowsById.values()) {
    const id = def.id.toString();
    if (added.has(id)) continue;
    cycleAt = id;
    break;
  }
  return { adds, reparented: [...reparent.keys()], cycleAt };
}
